import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Divider, Grid, Typography, Button } from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { Field } from '@campfire/field';
import { HoverText } from '@campfire/hover-link/lib';
import { DatePickerField } from '@campfire/date-picker/lib';
import { useFormikContext } from 'formik';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { SwitchField } from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/dialogs/field-task-item-dialog/additional-field-type-fields/SwitchField';

interface Props {
  isNew: boolean;
  disabled?: boolean;
}

export const FormDetailsCard = ({ isNew, disabled = false }: Props) => {
  const [showDescriptionField, setShowDescriptionField] = useState(false);
  const [showSuccessMessageField, setShowSuccessMessageField] = useState(false);
  const [showRedirectUrlField, setShowRedirectUrlField] = useState(false);
  const [showClosedMessageField, setShowClosedMessageField] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const { values, setFieldValue } = useFormikContext<any>();
  const { theme } = useCampfireTheme();

  const handleIsListedChange = () => {
    setFieldValue('isListed', !values.isListed);
  };

  const handleAllowMultipleSubmissionsChange = () => {
    setFieldValue('allowMultipleSubmissions', !values.allowMultipleSubmissions);
  };

  useEffect(() => {
    if (values.description) setShowDescriptionField(true);
  }, [values.description]);

  useEffect(() => {
    if (values.successMessage) setShowSuccessMessageField(true);
  }, [values.successMessage]);

  useEffect(() => {
    if (values.redirectUrl) setShowRedirectUrlField(true);
  }, [values.redirectUrl]);

  useEffect(() => {
    if (values.closedMessage) setShowClosedMessageField(true);
  }, [values.closedMessage]);

  return (
    <Card style={{ minHeight: isExpanded ? 'auto' : '150px' }}>
      <CardContent style={{ padding: 48, paddingTop: 32, paddingBottom: 32 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display='flex' alignItems='flex-start' justifyContent='space-between'>
              <Box flex={1}>
                <Typography style={{ fontSize: '13px', fontWeight: 400, color: '#999999', marginBottom: 8 }}>
                  Form Details
                </Typography>
                {!isExpanded && (
                  <>
                    <Typography style={{ fontSize: '20px', fontWeight: 600, color: '#333333', marginBottom: 8 }}>
                      {values.title || 'New Form'}
                    </Typography>
                    {values.description && (
                      <Typography style={{ fontSize: '14px', fontWeight: 400, color: '#666666', marginBottom: 8 }}>
                        {values.description}
                      </Typography>
                    )}
                    <Box display='flex' gap={2} flexWrap='wrap' marginTop={1}>
                      <Typography style={{ fontSize: '13px', color: '#999999' }}>
                        {values.isListed ? 'Public' : 'Unlisted'}
                      </Typography>
                      {values.dateClosed && (
                        <Typography style={{ fontSize: '13px', color: '#999999' }}>
                          Closes: {new Date(values.dateClosed).toLocaleDateString()}
                        </Typography>
                      )}
                      {values.allowMultipleSubmissions && (
                        <Typography style={{ fontSize: '13px', color: '#999999' }}>
                          Multiple submissions allowed
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>
              <Button
                variant='text'
                endIcon={isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                  fontSize: 13,
                  textDecoration: 'none',
                  color: theme.color.grey.neutral300,
                  textTransform: 'none',
                  padding: '6px 12px',
                  minWidth: '100px',
                }}
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </Button>
            </Box>
          </Grid>
          {isExpanded ? (
            <>
              <Grid item xs={12}>
                <Field
                  autoFocus
                  name='title'
                  label={isNew ? 'This form will be named' : 'Form name'}
                  placeholder='e.g., "Volunteer Application", "Event Registration", "Feedback Survey"'
                  required
                  fullWidth
                  variant='outlined'
                />
              </Grid>
              <Grid item xs={12}>
                {!showDescriptionField ? (
                  <HoverText onClick={() => setShowDescriptionField(true)} color='primary'>
                    {'Add description'}
                  </HoverText>
                ) : null}
              </Grid>
              {showDescriptionField ? (
                <Grid item xs={12}>
                  <Field fullWidth label='Description' name='description' variant='outlined' multiline rows={3} />
                </Grid>
              ) : null}

              <Grid item xs={12} style={{ padding: '25px 0' }}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Typography style={{ fontSize: '15px', fontWeight: 600, color: '#666666', paddingBottom: '10px' }}>
                  Options
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {!showSuccessMessageField ? (
                  <HoverText onClick={() => setShowSuccessMessageField(true)} color='primary'>
                    {'Add success message'}
                  </HoverText>
                ) : null}
              </Grid>
              {showSuccessMessageField ? (
                <Grid item xs={12} style={{ marginTop: '12px' }}>
                  <Field
                    fullWidth
                    label='Success Message'
                    name='successMessage'
                    variant='outlined'
                    placeholder='Thank you for submitting this form!'
                    helperText='This message will be shown to users after they submit the form'
                    multiline
                    rows={2}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                {!showRedirectUrlField ? (
                  <HoverText onClick={() => setShowRedirectUrlField(true)} color='primary'>
                    {'Add redirect URL'}
                  </HoverText>
                ) : null}
              </Grid>
              {showRedirectUrlField ? (
                <Grid item xs={12} style={{ marginTop: '12px' }}>
                  <Field
                    fullWidth
                    label='Redirect URL (Optional)'
                    name='redirectUrl'
                    variant='outlined'
                    placeholder='https://example.com/thank-you'
                    helperText='Redirect users to this URL after successful submission (leave blank to show success message)'
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} style={{ marginTop: '24px' }}>
                <DatePickerField
                  name='dateClosed'
                  label='Close Date (Optional)'
                  autoOk
                  fullWidth
                  inputVariant='outlined'
                  disabled={disabled}
                  helperText='The form will no longer accept submissions after this date'
                  clearable
                />
              </Grid>
              <Grid item xs={12}>
                {!showClosedMessageField ? (
                  <HoverText onClick={() => setShowClosedMessageField(true)} color='primary'>
                    {'Add closed message'}
                  </HoverText>
                ) : null}
              </Grid>
              {showClosedMessageField ? (
                <Grid item xs={12} style={{ marginTop: '12px' }}>
                  <Field
                    fullWidth
                    label='Closed Message (Optional)'
                    name='closedMessage'
                    variant='outlined'
                    placeholder='This form is no longer accepting submissions'
                    helperText='This message will be shown when the form is closed'
                    multiline
                    rows={2}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} style={{ marginTop: '12px' }}>
                <SwitchField
                  title='Listed / Public'
                  description={
                    values.isListed
                      ? 'This form is publicly listed and accessible to anyone with the link'
                      : 'This form is unlisted and only accessible to those with the direct link'
                  }
                  checked={values.isListed}
                  onChange={handleIsListedChange}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} style={{ marginTop: '12px' }}>
                <SwitchField
                  title='Allow Multiple Submissions'
                  description={
                    values.allowMultipleSubmissions
                      ? 'Users can submit this form multiple times'
                      : 'Users can only submit this form once'
                  }
                  checked={values.allowMultipleSubmissions}
                  onChange={handleAllowMultipleSubmissionsChange}
                  disabled={disabled}
                />
              </Grid>
              <Grid item style={{ paddingBottom: '25px' }}></Grid>
            </>
          ) : null}
        </Grid>
      </CardContent>
    </Card>
  );
};
